import { api } from "@/lib/api";
import {
  RegionDto,
  regionDtoListSchema,
  RegionDtoSchema,
  regionFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getRegionsQueryOptions = queryOptions({
  queryKey: ["regions"],
  queryFn: async () => {
    const result: Array<RegionDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/regions", regionDtoListSchema, {
        query: {
          page: page.toString(),
        },
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(regionFromDto);
  },
});

export function useGetRegions<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({ ...getRegionsQueryOptions, enabled: opts?.enabled });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getRegionsError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getRegionQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["regions", id],
    queryFn: () =>
      api
        .get("/api/regions/$id", RegionDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(regionFromDto),
  });
}

export function useGetRegion<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getRegionQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getRegionDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
